import * as THREE from 'three';
import HullComponent from './hull_component.js';
import { LoopSubdivision } from 'three-subdivide';
import * as materials from './materials.js';

// Number of segments in the nacelle-side curve.
// 1 = original single quad (sharp elbow), higher = smoother curve.
const NACELLE_CURVE_SEGMENTS = 24;

export default class Pylon extends HullComponent {
  constructor({ nacelle, engineering, name }) {
    super();
    this.group = new THREE.Group();
    this.geometry = {};
    this.mesh = {};
    this.nacelle = nacelle;
    this.engineering = engineering;
    this.name = name;

    return this;
  }


  update({
    nacelleForeOffset, // distance away from fore edge of nacelle hull 1.0 = Full aft
    nacelleAftOffset, // distance away from aft edge of nacelle hull 1.0 = Full fore
    engineeringForeOffset, // distance away from fore edge of engineering hull 1.0 = Full aft
    engineeringAftOffset, // distance away from aft edge of engineering hull 1.0 = Full fore})
    thickness,
    midpointOffset, //0-1 where 0 is engineering center and 1 is nacelle center, established where the elbow of the pylon is
    materialIndex=0,
    engineeringZOffset=0,
    curvature=0, // 0 = concentrated at midpoint (sharp elbow), 1 = evenly distributed (smooth arc)
  }){

    this.clear();

    let nacelle = this.nacelle;
    let engineering = this.engineering;

    let nacelleLength = nacelle.dimensions.y;
    let engineeringLength = engineering.dimensions.y;

    let nacelleCenterTop = nacelle.group.position.z;
    let nacelleCenterX = nacelle.group.position.x
    let nacelleCenterY = nacelle.group.position.y;
    let nacelleCenterZ = nacelle.group.position.z; // nacelleCenterTop + thickness * 0.5;

    let nacelleFore = nacelleCenterY + nacelleLength;
    let nacelleAft = nacelleCenterY;

    let engineeringAft = engineering.group.position.y;
    let engineeringFore = engineeringAft + engineeringLength;
    let engineeringCenterX = engineering.group.position.x;
    let engineeringCenterY = engineering.group.position.y;
    let engineeringCenterZ = engineering.group.position.z + engineeringZOffset;

    nacelleFore -= nacelleLength * nacelleForeOffset;
    nacelleAft += nacelleLength * nacelleAftOffset;

    engineeringFore -= engineeringLength * engineeringForeOffset;
    engineeringAft += engineeringLength * engineeringAftOffset;

    let midCenterX = (nacelle.group.position.x - engineering.group.position.x) * midpointOffset;
    let midCenterY = engineering.group.position.y;
    let midCenterZ = engineering.group.position.z + engineeringZOffset;
    let midFore = engineeringFore;
    let midAft = engineeringAft;

    let deltaX = nacelleCenterX - midCenterX;
    let deltaZ = nacelleCenterZ - midCenterZ;
    let angle = Math.atan2(deltaZ, deltaX);
    let thicknessX_nm = thickness * Math.sin(angle);
    let thicknessZ_nm = thickness * Math.cos(angle);
    if (deltaX < 0) {
      thicknessX_nm = -thicknessX_nm;
      thicknessZ_nm = -thicknessZ_nm;
    };

    deltaX = midCenterX - engineeringCenterX;
    deltaZ = midCenterZ - engineeringCenterZ;
    angle = Math.atan2(deltaZ, deltaX);
    let thicknessX_em = thickness * Math.sin(angle);
    let thicknessZ_em = thickness * Math.cos(angle);
    if (deltaX < 0) {
      thicknessX_em = -thicknessX_em;
      thicknessZ_em = -thicknessZ_em;
    };

    // ---- Engineering slab and nacelle curve are built directly into indexed geometry below ----

    // ---- Nacelle curve Bézier setup ----

    const N = NACELLE_CURVE_SEGMENTS; // number of segments = N+1 points

    // Bézier endpoints are always midpoint → nacelle.
    // The control point P1 blends between:
    //   curvature=0 → P1 on the straight line midpoint→nacelle (no bend, pure straight)
    //   curvature=1 → P1 at (nacelleCenterX, midCenterZ) (full horizontal-then-vertical arc)
    // This keeps the curve G1-continuous at all curvature values — no kink.
    const bezP0x = midCenterX, bezP0z = midCenterZ;
    const bezP2x = nacelleCenterX, bezP2z = nacelleCenterZ;
    // Straight-line midpoint of P0→P2:
    const straightP1x = (bezP0x + bezP2x) * 0.5;
    const straightP1z = (bezP0z + bezP2z) * 0.5;
    // Full-arc control point (horizontal departure from midpoint):
    const arcP1x = nacelleCenterX, arcP1z = midCenterZ;
    // Blend by curvature:
    const bezP1x = straightP1x + (arcP1x - straightP1x) * curvature;
    const bezP1z = straightP1z + (arcP1z - straightP1z) * curvature;

    // curvature controls the shape of the curve by blending the Bézier control point
    // between the straight-line midpoint (curvature=0, sharp elbow) and the full arc
    // control point (curvature=1, smooth sweep to nacelle).
    // All N+1 rings are always evenly distributed t=0..1 so there is never a tangent
    // discontinuity — the geometry is always G1-continuous.

    // Canonical side orientation: "side A" is always the +Z side of the pylon
    // regardless of which direction the nacelle is in X. Without this, the
    // perpendicular offset flips sign on the starboard pylon (nacelleCenterX < 0),
    // swapping which face gets V=0 vs V=0.66 and mirroring the UV vertically.
    // Multiplying the offset by this sign keeps side A = +Z on both pylons.
    const sideSign = nacelleCenterX >= 0 ? 1 : -1;

    // ---- Build indexed geometry ----
    // Cross-section is a rectangle: two "side" faces (sideA, sideB along the
    // thickness axis) and two "span" faces (fore, aft along the pylon length).
    //
    // UV unwrap: cylindrical / perimeter unwrap so every face gets proportional
    // coverage.  V goes around the perimeter; U goes along the pylon arc-length.
    //   Perimeter order: foreA → foreB (fore face)
    //                    foreB → aftB  (sideB face)
    //                    aftB  → aftA  (aft face)
    //                    aftA  → foreA (sideA face) — seam: foreA repeated at V=1
    //
    // Each station has 5 vertices (the seam vertex foreA is duplicated for V=0 & V=1):
    //   0: foreA  V=0          (shared: fore face left edge & sideA face right edge at seam)
    //   1: foreB  V=vForeB     (shared: fore face right edge & sideB face left edge)
    //   2: aftB   V=vAftB      (shared: sideB face right edge & aft face left edge)
    //   3: aftA   V=vAftA      (shared: aft face right edge & sideA face left edge)
    //   4: foreA' V=1          (seam duplicate of vert 0, same position, V=1)
    //
    // Smooth normals across all longitudinal edges; UV seam only at foreA.

    // Perimeter fractions based on physical dimensions:
    //   fore/aft face width  = 2*offPerp  (perpendicular thickness, varies per ring)
    //   sideA/sideB face width = fore-aft span  (nacelle fore-aft length, varies per ring)
    // For simplicity we compute V breakpoints per station from actual geometry.
    // (They vary slightly along the curve but that's correct — matches real proportions.)

    const positions = [];
    const uvs       = [];
    const indices   = [];

    // offPerp: perpendicular half-thickness (length of fore/aft face = 2*offPerp)
    // span:    fore-aft length of the station (length of sideA/sideB face)
    function stationVBreaks(offPerp, span) {
      const faceW = 2 * offPerp;   // fore face width = aft face width
      const sideW = span;           // sideA width = sideB width
      const perim = 2 * faceW + 2 * sideW || 1;
      const vForeB = faceW / perim;
      const vAftB  = (faceW + sideW) / perim;
      const vAftA  = (2 * faceW + sideW) / perim;
      return { vForeB, vAftB, vAftA };
    }

    // addStation takes the 4 corner positions and the U value.
    // Returns the base index (points to vert 0 = foreA).
    function addStation(foreA, foreB, aftA, aftB, u) {
      const base = positions.length / 3;

      // Perimeter V breakpoints from actual geometry
      const offPerp = foreA.distanceTo(foreB) * 0.5;
      const span    = foreA.distanceTo(aftA);
      const { vForeB, vAftB, vAftA } = stationVBreaks(offPerp, span);

      positions.push(foreA.x, foreA.y, foreA.z); // 0: foreA  V=0
      positions.push(foreB.x, foreB.y, foreB.z); // 1: foreB  V=vForeB
      positions.push( aftB.x,  aftB.y,  aftB.z); // 2: aftB   V=vAftB
      positions.push( aftA.x,  aftA.y,  aftA.z); // 3: aftA   V=vAftA
      positions.push(foreA.x, foreA.y, foreA.z); // 4: foreA' V=1  (seam duplicate)

      uvs.push(u, 0.0,  u, vForeB,  u, vAftB,  u, vAftA,  u, 1.0);
      return base;
    }

    function addQuadStrip(b0, b1) {
      // Each station: 0=foreA(V=0), 1=foreB, 2=aftB, 3=aftA, 4=foreA'(V=1)
      // Fore face   (foreA → foreB):
      indices.push(b0+0, b0+1, b1+0,  b0+1, b1+1, b1+0);
      // SideB face  (foreB → aftB):
      indices.push(b0+1, b0+2, b1+1,  b0+2, b1+2, b1+1);
      // Aft face    (aftB → aftA):
      indices.push(b0+2, b0+3, b1+2,  b0+3, b1+3, b1+2);
      // SideA face  (aftA → foreA', seam):
      indices.push(b0+3, b0+4, b1+3,  b0+4, b1+4, b1+3);
    }

    // --- Arc-length U values ---
    // Always sample the full Bézier t=0..1 since the last ring always reaches the nacelle.
    const ARC_SAMPLES = 64;
    const arcSampleLen = [0];
    let prevSx = bezP0x, prevSz = bezP0z;
    for (let s = 1; s <= ARC_SAMPLES; s++) {
      const ts = s / ARC_SAMPLES;
      const mts = 1 - ts;
      const sx = mts*mts*bezP0x + 2*mts*ts*bezP1x + ts*ts*bezP2x;
      const sz = mts*mts*bezP0z + 2*mts*ts*bezP1z + ts*ts*bezP2z;
      const ddx = sx - prevSx, ddz = sz - prevSz;
      arcSampleLen.push(arcSampleLen[arcSampleLen.length-1] + Math.sqrt(ddx*ddx + ddz*ddz));
      prevSx = sx; prevSz = sz;
    }
    const totalCurveArc = arcSampleLen[ARC_SAMPLES];

    function tToArcFrac(t) {
      if (t <= 0) return 0;
      if (t >= 1) return 1;
      const idx = t * ARC_SAMPLES;
      const lo = Math.floor(idx), hi = Math.min(lo + 1, ARC_SAMPLES);
      const frac = idx - lo;
      return (arcSampleLen[lo] + frac * (arcSampleLen[hi] - arcSampleLen[lo])) / totalCurveArc;
    }

    const dxEM = midCenterX - engineeringCenterX;
    const dzEM = midCenterZ - engineeringCenterZ;
    const engArc = Math.sqrt(dxEM*dxEM + dzEM*dzEM);
    const totalArc = engArc + totalCurveArc || 1;
    const uEng = 0;
    const uMid = engArc / totalArc;

    // All N+1 rings always span t=0..1 — smooth, no kink.
    function ringT(i) {
      return i / N;
    }

    const uCurve = [];
    for (let i = 0; i <= N; i++) {
      uCurve.push(uMid + tToArcFrac(ringT(i)) * (1 - uMid));
    }

    // --- Build curve rings 0..N ---
    // Ring 0 (t=0) lands exactly at the midpoint (bezP0), so it serves as the shared
    // join station between the engineering slab and the curve — one set of vertices,
    // shared by both strips, so computeVertexNormals() averages across the seam.
    let prevBase = -1;
    let ring0Base = -1;
    for (let i = 0; i <= N; i++) {
      const t = ringT(i);
      const mt = 1 - t;

      const cx = mt*mt*bezP0x + 2*mt*t*bezP1x + t*t*bezP2x;
      const cz = mt*mt*bezP0z + 2*mt*t*bezP1z + t*t*bezP2z;
      const fore = (1-t)*midFore + t*nacelleFore;
      const aft  = (1-t)*midAft  + t*nacelleAft;

      const tdx = 2*(1-t)*(bezP1x-bezP0x) + 2*t*(bezP2x-bezP1x);
      const tdz = 2*(1-t)*(bezP1z-bezP0z) + 2*t*(bezP2z-bezP1z);
      const tlen = Math.sqrt(tdx*tdx + tdz*tdz) || 1;
      // sideSign ensures the +offZ side is always "side A" (+Z face) on both pylons
      const offX = (tdz/tlen) * thickness * sideSign;
      const offZ = (-tdx/tlen) * thickness * sideSign;

      const curBase = addStation(
        new THREE.Vector3(cx - offX, fore, cz - offZ), // foreA
        new THREE.Vector3(cx + offX, fore, cz + offZ), // foreB
        new THREE.Vector3(cx - offX, aft,  cz - offZ), // aftA
        new THREE.Vector3(cx + offX, aft,  cz + offZ), // aftB
        uCurve[i]
      );

      if (i === 0) {
        ring0Base = curBase; // shared join with slab
      } else {
        addQuadStrip(prevBase, curBase);
      }
      prevBase = curBase;
    }

    // --- Station: engineering end ---
    // Built after curve rings so ring0Base is known; connected directly to ring 0.
    const engBase = addStation(
      new THREE.Vector3(engineeringCenterX, engineeringFore, engineeringCenterZ + thickness), // foreA
      new THREE.Vector3(engineeringCenterX, engineeringFore, engineeringCenterZ - thickness), // foreB
      new THREE.Vector3(engineeringCenterX, engineeringAft,  engineeringCenterZ + thickness), // aftA
      new THREE.Vector3(engineeringCenterX, engineeringAft,  engineeringCenterZ - thickness), // aftB
      uEng
    );

    // Slab: engineering end → ring 0 (shared midpoint station)
    addQuadStrip(engBase, ring0Base);

    // Engineering end cap (0=foreA, 1=foreB, 2=aftB, 3=aftA — winding outward)
    indices.push(engBase+0, engBase+3, engBase+1,  engBase+1, engBase+3, engBase+2);

    // Nacelle end cap (winding outward, opposite direction)
    indices.push(prevBase+0, prevBase+1, prevBase+3,  prevBase+1, prevBase+2, prevBase+3);

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    this.geometry.setAttribute('uv',       new THREE.Float32BufferAttribute(uvs, 2));
    this.geometry.setIndex(indices);
    this.geometry.computeVertexNormals();

    const iterations = 1;

    const params = {
        split:          true,       // optional, default: true
        uvSmooth:       false,      // optional, default: false
        preserveEdges:  false,      // optional, default: false
        flatOnly:       true,      // optional, default: false
        maxTriangles:   Infinity,   // optional, default: Infinity
    };
    
    // this.geometry = LoopSubdivision.modify(this.geometry, iterations, params);

    this.mesh = new THREE.Mesh( this.geometry, materials.pylonMaterial[materialIndex].clone() );
    this.mesh.name = this.name;
    this.group.add( this.mesh );
  }

  clear(){
    if (this.geometry['dispose']) {
      this.geometry.dispose();
      for (var i = this.group.children.length - 1; i >= 0; i--) {
        this.group.remove(this.group.children[i]);
      }
    }
  }
}